import { JSX, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { isValid as isValidDate } from "date-fns";

import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";

import FeedbackButton from "lib/components/button/FeedbackButton";

import { asTitle } from "utils/Utils";
import DatePicker from "utils/elements/DatePicker";

import { BlueprintInfo } from "core/blueprint/base/BlueprintService";
import { getExtractBlueprints } from "extractblueprint/ExtractBlueprintService";
import ComboBox from "lib/ui/textfield/ComboBox";

interface SelectExtractBlueprintDialogProps {
	onClose: () => void;
	onConfirm: (
		name: string,
		extract: number,
		asOf: Date | null
	) => Promise<void>;
}

export default function SelectExtractBlueprintDialog(
	props: Readonly<SelectExtractBlueprintDialogProps>
): JSX.Element {
	const [t] = useTranslation();

	const [name, setName] = useState("");
	const [extract, setExtract] = useState<BlueprintInfo | null>(null);
	// The day whose state the PDF renders; null means the current state.
	const [asOf, setAsOf] = useState<Date | null>(null);
	const [extractBlueprints, setExtractBlueprints] = useState<BlueprintInfo[]>(
		[]
	);

	useEffect(() => {
		getExtractBlueprints().then(bps =>
			setExtractBlueprints(
				bps.sort((a, b) => a.name.localeCompare(b.name))
			)
		);
	}, []);

	function isValid(): boolean {
		return name !== "" && extract !== null;
	}

	return (
		<Dialog open fullWidth maxWidth="xs" onClose={props.onClose}>
			<DialogTitle id="dialog-title">
				{asTitle(t("createExtract"))}
			</DialogTitle>
			<DialogContent>
				<Grid container direction="column" spacing={2}>
					<Grid item>
						<TextField
							required
							fullWidth
							label={t("name")}
							value={name}
							onChange={e => setName(e.target.value)}
							variant="standard"></TextField>
					</Grid>
					<Grid item>
						<ComboBox
							label={t("extractBlueprint")}
							options={extractBlueprints}
							value={extract}
							getOptionLabel={bp => bp.name}
							getOptionKey={bp => bp.id}
							onChange={setExtract}
						/>
					</Grid>
					<Grid item>
						<DatePicker
							label={asTitle(t("asOf"))}
							value={asOf}
							disableFuture
							inputProps={{
								variant: "standard",
								fullWidth: true,
								helperText: t("asOfEmpty")
							}}
							// A partially typed field arrives as an Invalid Date; that
							// is no date.
							onChange={date =>
								setAsOf(date !== null && isValidDate(date) ? date : null)
							}
						/>
					</Grid>
				</Grid>
			</DialogContent>
			<DialogActions>
				<Button variant="outlined" onClick={props.onClose} autoFocus>
					{t("cancel")}
				</Button>
				<FeedbackButton
					variant="contained"
					label={t("create")}
					disabled={!isValid()}
					onClick={() => props.onConfirm(name, extract?.id ?? 0, asOf)}
				/>
			</DialogActions>
		</Dialog>
	);
}
